import { Input } from "@/components/elements/Input";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Button } from "@/components/elements/Button";
import { Box, Stack, styled, Modal, Typography, Grid, Divider } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { upsertMonthlyTargetApi } from "@/api/services/main/dashboard";
import { useGetMonthlyTargetSchema } from "@/features/dashboard/hooks/useGetMonthlyTargetSchema";
import { TMonthlyTargetForm } from "@/features/dashboard/types/MonthlyTargetForm";
import { background, black, text } from "@/theme/colors";
import { UseVisibleReturn } from "@/hooks/useVisible";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { setErrorForm, showError } from "@/utils/error";
import { Textarea } from "@/components/elements/Textarea";
import { IconCloseModal, IconSave } from "@/assets/icons";
import { IconBox } from "@/components/elements/IconBox";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { convertMonthlyTargetFormDataReq } from "@/features/dashboard/utils/convertMonthlyTargetFormData";
import dayjs from "dayjs";
import { isEqual } from "lodash";

type TMonthlyTargetModalProps = {
  modal: UseVisibleReturn;
  targetMonth: string; // YYYY-MM format
  defaultValues?: Partial<TMonthlyTargetForm>;
};

export const MonthlyTargetModal: FC<TMonthlyTargetModalProps> = ({ modal, targetMonth, defaultValues }) => {
  const { monthlyTargetSchema } = useGetMonthlyTargetSchema();
  const queryClient = useQueryClient();
  const [showConfirmLeave, setShowConfirmLeave] = useState(false);

  // Title prefix: "今月" for current month, "{M}月" otherwise.
  const targetMonthDayjs = dayjs(targetMonth, "YYYY-MM");
  const monthLabel = targetMonthDayjs.isSame(dayjs(), "month") ? "今月" : `${targetMonthDayjs.format("M")}月`;

  const methods = useForm<TMonthlyTargetForm>({
    defaultValues,
    resolver: yupResolver(monthlyTargetSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const { control, reset, watch } = methods;

  // Load default values when modal opens or data changes
  useEffect(() => {
    reset({
      targetJobs: defaultValues?.targetJobs ?? null,
      targetCases: defaultValues?.targetCases ?? null,
      targetOpenJobs: defaultValues?.targetOpenJobs ?? null,
      targetTalents: defaultValues?.targetTalents ?? null,
      targetMatchingBeforeProposals: defaultValues?.targetMatchingBeforeProposals ?? null,
      targetMatchingBeforeProposalRate: defaultValues?.targetMatchingBeforeProposalRate ?? null,
      targetMatchingRecommendations: defaultValues?.targetMatchingRecommendations ?? null,
      targetMatchingRecommendationRate: defaultValues?.targetMatchingRecommendationRate ?? null,
      targetMatchingInterviews: defaultValues?.targetMatchingInterviews ?? null,
      targetMatchingInterviewRate: defaultValues?.targetMatchingInterviewRate ?? null,
      targetMatchingOffers: defaultValues?.targetMatchingOffers ?? null,
      targetMatchingOfferRate: defaultValues?.targetMatchingOfferRate ?? null,
      targetMatchingAcceptances: defaultValues?.targetMatchingAcceptances ?? null,
      targetMatchingAcceptanceRate: defaultValues?.targetMatchingAcceptanceRate ?? null,
      memo: defaultValues?.memo ?? null,
    });
  }, [defaultValues, targetMonth, reset]);

  // Check if form values are different from default values
  const hasChanges = () => {
    const currentValues = watch();

    // Helper to normalize a single value (convert number-string to number)
    const normalizeValue = (value: string | number | null | undefined): number | string | null => {
      if (value === null || value === undefined || value === "") {
        return null;
      }
      const num = typeof value === "string" ? Number(value.replace(/,/g, "")) : value;
      return isNaN(num) ? value : num;
    };

    // Normalize form object for comparison
    const normalizeFormData = (data?: Partial<TMonthlyTargetForm>) => ({
      targetJobs: normalizeValue(data?.targetJobs),
      targetCases: normalizeValue(data?.targetCases),
      targetOpenJobs: normalizeValue(data?.targetOpenJobs),
      targetTalents: normalizeValue(data?.targetTalents),
      targetMatchingBeforeProposals: normalizeValue(data?.targetMatchingBeforeProposals),
      targetMatchingBeforeProposalRate: normalizeValue(data?.targetMatchingBeforeProposalRate),
      targetMatchingRecommendations: normalizeValue(data?.targetMatchingRecommendations),
      targetMatchingRecommendationRate: normalizeValue(data?.targetMatchingRecommendationRate),
      targetMatchingInterviews: normalizeValue(data?.targetMatchingInterviews),
      targetMatchingInterviewRate: normalizeValue(data?.targetMatchingInterviewRate),
      targetMatchingOffers: normalizeValue(data?.targetMatchingOffers),
      targetMatchingOfferRate: normalizeValue(data?.targetMatchingOfferRate),
      targetMatchingAcceptances: normalizeValue(data?.targetMatchingAcceptances),
      targetMatchingAcceptanceRate: normalizeValue(data?.targetMatchingAcceptanceRate),
      memo: data?.memo ? data.memo : null,
    });

    return !isEqual(normalizeFormData(currentValues), normalizeFormData(defaultValues));
  };

  const handleCloseAttempt = () => {
    if (hasChanges()) {
      setShowConfirmLeave(true);
    } else {
      onClose();
    }
  };

  const onClose = () => {
    methods.reset();
    modal.close();
    setShowConfirmLeave(false);
  };

  const handleSubmit = async (data: TMonthlyTargetForm) => {
    const payload = convertMonthlyTargetFormDataReq(data, targetMonth);

    try {
      await upsertMonthlyTargetApi(payload);
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.DASHBOARD.FETCH_MONTHLY_TARGET],
      });

      toast.success(API_SUCCESS_MESSAGE);
      modal.close();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <>
      <Modal open={modal.visible} hideBackdrop={false} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <ModalContainer>
          <Form methods={methods} onSubmit={handleSubmit} schema={monthlyTargetSchema}>
            <Stack
              bgcolor={background.disable}
              direction="row"
              height="56px"
              alignItems="center"
              justifyContent="space-between"
              pl={3}
              pr={3}
              sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}
            >
              <Typography variant="body14Light" color={text.primary}>
                {`${monthLabel}の実績値と目標値／マッチング実績値と目標値 ｜ 目標設定`}
              </Typography>
              <IconBox onClick={handleCloseAttempt}>
                <IconCloseModal color={black[30]} />
              </IconBox>
            </Stack>
            <Stack sx={{ p: "32px", gap: "16px" }}>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={3}>
                  <FormField control={control} name="targetJobs">
                    <Input placeholder="入力してください" endAdornment="件" clearable={false} />
                  </FormField>
                </Grid>
                <Grid item xs={3}>
                  <FormField control={control} name="targetCases">
                    <Input placeholder="入力してください" endAdornment="件" clearable={false} />
                  </FormField>
                </Grid>
                <Grid item xs={3}>
                  <FormField control={control} name="targetTalents">
                    <Input placeholder="入力してください" endAdornment="人" clearable={false} />
                  </FormField>
                </Grid>
                <Grid item xs={3}>
                  <FormField control={control} name="targetOpenJobs">
                    <Input placeholder="入力してください" endAdornment="件" clearable={false} />
                  </FormField>
                </Grid>

                <Grid item xs={6}>
                  <FormField control={control} name="targetMatchingBeforeProposals">
                    <Input placeholder="入力してください" endAdornment="件" clearable={false} />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField control={control} name="targetMatchingBeforeProposalRate">
                    <Input placeholder="入力してください" endAdornment="%" clearable={false} />
                  </FormField>
                </Grid>

                <Grid item xs={6}>
                  <FormField control={control} name="targetMatchingRecommendations">
                    <Input placeholder="入力してください" endAdornment="件" clearable={false} />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField control={control} name="targetMatchingRecommendationRate">
                    <Input placeholder="入力してください" endAdornment="%" clearable={false} />
                  </FormField>
                </Grid>

                <Grid item xs={6}>
                  <FormField control={control} name="targetMatchingInterviews">
                    <Input placeholder="入力してください" endAdornment="件" clearable={false} />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField control={control} name="targetMatchingInterviewRate">
                    <Input placeholder="入力してください" endAdornment="%" clearable={false} />
                  </FormField>
                </Grid>

                <Grid item xs={6}>
                  <FormField control={control} name="targetMatchingOffers">
                    <Input placeholder="入力してください" endAdornment="件" clearable={false} />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField control={control} name="targetMatchingOfferRate">
                    <Input placeholder="入力してください" endAdornment="%" clearable={false} />
                  </FormField>
                </Grid>

                <Grid item xs={6}>
                  <FormField control={control} name="targetMatchingAcceptances">
                    <Input placeholder="入力してください" endAdornment="件" clearable={false} />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField control={control} name="targetMatchingAcceptanceRate">
                    <Input placeholder="入力してください" endAdornment="%" clearable={false} />
                  </FormField>
                </Grid>

                <Grid item xs={12}>
                  <FormField control={control} name="memo">
                    <Textarea />
                  </FormField>
                </Grid>
              </Grid>
            </Stack>
            <Divider />
            <Stack direction="row" alignItems="center" justifyContent="space-between" p={2}>
              <Button variant="ghost" onClick={handleCloseAttempt}>
                キャンセル
              </Button>
              <Button variant="primary" type="submit" startIcon={<IconSave />}>
                目標値を保存
              </Button>
            </Stack>
          </Form>
        </ModalContainer>
      </Modal>

      <ConfirmSaveModal open={showConfirmLeave} onClose={() => setShowConfirmLeave(false)} onOk={onClose}>
        <Typography>入力が完了していません。このまま戻ってもよろしいですか？</Typography>
      </ConfirmSaveModal>
    </>
  );
};

const ModalContainer = styled(Box)`
  width: 960px;
  max-width: 95vw;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  max-height: 90vh;
  outline: none;
  &:focus {
    outline: none;
  }
`;
